import { Nullable } from '../../../types/common'
import { hideElement, showElement } from '../../../utils/dom'
import { BaseComponent } from '../../base-component'
import { ProductCard } from './product-card'
import { capitalize, $el, $list, $elParent } from '../../../utils/utils'
import { debounce } from 'src/scripts/utils/debounce'

const HIDDEN_COUNT_SELECTOR = '[data-color-swatches-hidden-count]'
const LABEL_SELECTOR = '[data-color-swatches-label]'
const ITEM_SELECTOR = '[data-color-swatches-item]'
const PRODUCT_CARD_SELECTOR = 'product-card'

export class ColorSwatches extends BaseComponent {
  productCard: Nullable<ProductCard>
  currentVisibleImage: Nullable<HTMLElement>
  images: HTMLImageElement[]

  constructor() {
    super()
    this.productCard = $elParent(PRODUCT_CARD_SELECTOR, this)
    this.images = $list(
      'img[data-variant-id], [data-main-media]',
      this.productCard,
    )
  }

  mountComponent(): void {
    this.setOffsets()
    this.update()
    this.addListener(this, 'mousemove', this.handleSwatchEnter)
    this.addListener(this, 'mouseleave', this.handleSwatchesLeave)
  }

  handleSwatchesLeave = debounce((): void => {
    this.images.forEach(img => {
      this.setImageVisible(img, img.hasAttribute('data-main-media'))
    })

    this.reset()
  }, 100)

  handleSwatchEnter = debounce((event: MouseEvent): void => {
    if (!this.productCard) {
      return
    }

    const item = $elParent(ITEM_SELECTOR, event.target)

    if (!item) {
      this.leave()
      return
    }

    const variantId = item.dataset.variantId
    const image = $el(`img[data-variant-id="${variantId}"]`, this.productCard)
    const color = item.dataset.color

    if (!image || !this.productCard.getMainMedia() || !color) {
      return
    }

    this.updateLabel(color)
    this.images.forEach(img => {
      this.setImageVisible(img, img === image)
    })
    this.setHoverStatus(true)
    this.currentVisibleImage = image
  }, 10)

  leave = (): void => {
    if (this.currentVisibleImage) {
      this.setImageVisible(this.currentVisibleImage, false)
    }

    if (this.productCard.getMainMedia()) {
      this.setImageVisible(this.productCard.getMainMedia(), true)
    }

    this.reset()
  }

  reset = () => {
    this.setHoverStatus(false)
    this.updateLabel('')
  }

  update(): void {
    this.updateSwatchesVisible()
    this.updateCount()
    this.updateDetailsInner()
  }

  updateDetailsInner() {
    const detailsInner = $elParent('[data-product-details-inner]', this)

    if (!detailsInner) {
      return
    }

    if (detailsInner.offsetHeight < this.offsetHeight) {
      detailsInner.style.minHeight = `${this.offsetHeight}px`
    }
  }

  setImageVisible(img: HTMLElement, isVisible: boolean): void {
    img.classList.toggle('product-card__img--visible', isVisible)
  }

  updateSwatchesVisible(): void {
    const items = $list(ITEM_SELECTOR, this)

    items.forEach(swatch => {
      const rightPosition = swatch.offsetLeft + swatch.offsetWidth
      const parentNode = this.parentNode as HTMLElement
      const maxWidth = parentNode.offsetWidth - swatch.offsetWidth

      const isHidden = rightPosition > maxWidth
      swatch.classList.toggle('hidden-swatch', isHidden)
      swatch.setAttribute('tabindex', isHidden ? '-1' : '0')
    })
  }

  setOffsets() {
    const items = $list(ITEM_SELECTOR, this)
    items.forEach(swatch => {
      swatch.style.left = `${swatch.offsetLeft}px`
    })
  }

  updateCount(): void {
    const items = $list(ITEM_SELECTOR, this)
    const hiddenCount = $el(HIDDEN_COUNT_SELECTOR, this)

    if (!hiddenCount) {
      return
    }

    const hiddenCountLabel = $el(
      '[data-color-swatches-hidden-count-label]',
      this,
    )

    if (!hiddenCountLabel) {
      return
    }

    const notHiddenSwatches = items.filter(
      item => !item.classList.contains('hidden-swatch'),
    )
    const count = +(this.dataset.count || 0) - notHiddenSwatches.length

    if (count > 0) {
      showElement(hiddenCount)
      hiddenCountLabel.innerText = `+${count}`
    } else {
      hideElement(hiddenCount)
      hiddenCountLabel.innerText = ''
    }
  }

  updateLabel(color: string): void {
    const label = $el(LABEL_SELECTOR, this)

    if (label) {
      label.innerText = color ? capitalize(color) : ''
    }
  }

  setHoverStatus(isHover: boolean) {
    this.productCard?.classList.toggle('is-color-swatch-hover', isHover)
  }
}
